import type { ConversationMessage, MessageReaction } from '@/lib/gupshup-client';

export type MediaData = {
  url: string;
  contentType?: string;
  filename?: string;
};

/** UI-shaped message consumed by MessageBubble/MessageContent (built by transformMessages). */
export type Message = {
  id: string;
  direction: 'inbound' | 'outbound';
  content: string;
  createdAt: string;
  status?: string;
  phoneNumber: string;
  hasMedia: boolean;
  mediaData?: MediaData;
  reactions?: { customer?: MessageReaction; business?: MessageReaction };
  /** A synthetic confirm tap the agent made from a customer's 👍 (D9) — it has no WhatsApp message to react to. */
  fromReaction?: boolean;
  filename?: string | null;
  mimeType?: string | null;
  messageType?: string;
  caption?: string | null;
  metadata?: {
    mediaId?: string;
    caption?: string;
    role?: 'human' | 'assistant';
    externalId?: string;
    location?: {
      latitude: number;
      longitude: number;
    };
  };
  interactiveButtons?: Array<{ id: string; title: string }>;
  interactiveCtaButton?: { text: string; url: string };
  isLocationRequest?: boolean;
  interactiveFlow?: { bodyText?: string; ctaText?: string };
  /** Incoming list-picker reply (interactive list_reply). */
  listReply?: { id?: string; title?: string; description?: string };
  /** Incoming WhatsApp Flow completion (interactive nfm_reply). */
  nfmReply?: { name?: string; body?: string; responseJson?: unknown; responseJsonRaw?: string };
  /** Shared contact cards (contacts message). */
  contacts?: Array<{ name?: string; phones?: Array<{ phone?: string; type?: string }>; emails?: string[] }>;
  /** Outgoing template message details. */
  template?: { templateId?: string; params?: unknown[]; mediaUrl?: string };
  /** Marketing template munchies-api sent (campaign or automation), not a reply to the customer. */
  isCampaign?: boolean;
  /** Why the provider failed this outgoing message (Meta error code + reason), when it said. */
  deliveryError?: { code?: string; reason?: string };
  /**
   * Set when this message quotes another (WhatsApp "reply to", or a button tap on one of ours).
   * `externalIds` are every id the quoted message may be stored under; see `findQuotedMessage`.
   */
  replyTo?: { externalIds: string[] };
};

const MEDIA_TYPES = ['image', 'audio', 'video', 'document', 'sticker'];

/**
 * The failure reason of an outgoing message: the provider's `failed` status (`deliveryError`), or the
 * send-time error of a message that never left (`sendError`). Undefined when neither says anything.
 */
function deliveryErrorOf(meta: ConversationMessage['externalMetadata']): Message['deliveryError'] {
  const { code, reason } = meta?.deliveryError ?? {};
  if (code || reason) return { code, reason };
  return typeof meta?.sendError === 'string' && meta.sendError ? { reason: meta.sendError } : undefined;
}

/** The ids a quoting message names, deduped and in lookup order; empty when it quotes nothing. */
function replyToIdsOf(meta: ConversationMessage['externalMetadata']): string[] {
  const ids = [meta?.context?.id, meta?.context?.gsId, meta?.replyToExternalId];
  return [...new Set(ids.filter((id): id is string => typeof id === 'string' && id.length > 0))];
}

/**
 * The loaded message `message` quotes, or null when it is on a page not loaded yet (or never
 * stored). The lookup is by `externalId`: outgoing rows may be keyed by WhatsApp's id or by
 * Gupshup's depending on when they were stored, which is why `replyTo` carries both.
 */
export function findQuotedMessage(message: Message, byExternalId: Map<string, Message>): Message | null {
  for (const id of message.replyTo?.externalIds ?? []) {
    const quoted = byExternalId.get(id);
    if (quoted && quoted.id !== message.id) return quoted;
  }
  return null;
}

/** Index loaded messages by their provider id for quote lookups. */
export function indexByExternalId(messages: Message[]): Map<string, Message> {
  const index = new Map<string, Message>();
  for (const message of messages) {
    const externalId = message.metadata?.externalId;
    if (externalId) index.set(externalId, message);
  }
  return index;
}

/** Map a raw API message into the UI Message shape. */
export function toUiMessage(msg: ConversationMessage): Message {
  const meta = msg.externalMetadata;
  const hasMediaType = MEDIA_TYPES.includes(msg.type);
  const mediaUrl = hasMediaType ? meta?.media?.url : undefined;
  const mediaCaption = hasMediaType ? meta?.media?.caption : undefined;
  const mimeType = hasMediaType ? meta?.media?.mimeType : undefined;

  const locationData = msg.type === 'location' ? meta?.location : undefined;

  const isInteractive = msg.type === 'interactive';
  const interactiveSubtype = isInteractive ? meta?.type : undefined;
  const interactiveButtons =
    isInteractive && Array.isArray(meta?.buttons) ? (meta?.buttons as Array<{ id: string; title: string }>) : undefined;
  const interactiveCtaButton =
    isInteractive && meta?.button
      ? {
          text: meta.button.text as string,
          url: (meta.button.actionUrl || meta.button.url) as string,
        }
      : undefined;
  const isTemplate = interactiveSubtype === 'template' || (msg.type !== 'interactive' && meta?.type === 'template');
  const replyToIds = replyToIdsOf(meta);

  return {
    id: msg.id,
    direction: msg.direction === 'outgoing' ? 'outbound' : 'inbound',
    content: msg.content,
    createdAt: msg.createdAt,
    status: msg.status,
    phoneNumber: '',
    hasMedia: hasMediaType && !!mediaUrl,
    mediaData: hasMediaType && mediaUrl ? { url: mediaUrl, contentType: mimeType } : undefined,
    messageType: msg.type || 'text',
    caption: mediaCaption,
    reactions: meta?.reactions,
    fromReaction: meta?.source === 'reaction',
    metadata: {
      role: msg.role,
      externalId: msg.externalId,
      location: locationData,
    },
    interactiveButtons,
    interactiveCtaButton,
    isLocationRequest: interactiveSubtype === 'location_request_message',
    interactiveFlow: interactiveSubtype === 'flow' ? { bodyText: meta?.bodyText, ctaText: meta?.ctaText } : undefined,
    listReply: isInteractive && meta?.listReply ? meta.listReply : undefined,
    nfmReply: interactiveSubtype === 'nfm_reply' && meta?.nfmReply ? meta.nfmReply : undefined,
    contacts: meta?.contacts,
    template: isTemplate ? { templateId: meta?.templateId, params: meta?.params, mediaUrl: meta?.mediaUrl } : undefined,
    isCampaign: msg.senderKind === 'campaign',
    deliveryError: deliveryErrorOf(meta),
    ...(replyToIds.length > 0 ? { replyTo: { externalIds: replyToIds } } : {}),
  };
}
